import { FC, ChangeEventHandler, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { BlogCTAValuePropositionVariants, Experiment } from 'src/utils/convert';
import BlogValuePropositionCard from './BlogValuePropositionCard';
import { useConvertExperiment } from 'src/hooks';
import InputBox from '@z-components/atoms/InputBox';
import SkeletonLoader from './SkeletonLoader';
import type { InternalBlogPost } from 'src/services/internal-blog';

import style from './BlogCTAValuePropositionVariantSwitcher.module.scss';

export type BlogCTAValuePropositionVariantSwitcherProps = {
  onCTAClick: MouseEventHandler;
  onInputChange: ChangeEventHandler;
  ctaValueProposition: InternalBlogPost['valuePropositions']['ctaValueProposition'];
  gtmClasses?: string;
  dataGtmClick?: string;
};

const BlogCTAValuePropositionVariantSwitcher: FC<BlogCTAValuePropositionVariantSwitcherProps> = ({
  children,
  onCTAClick,
  onInputChange,
  gtmClasses,
  dataGtmClick,
  ctaValueProposition,
}) => {
  const { experiment, isConvertLoading } = useConvertExperiment(Experiment.BlogValueProposition);
  const variant = experiment?.getVariant();
  const { t } = useTranslation();
  let ReactContent = <>{children}</>;
  switch (variant) {
    case BlogCTAValuePropositionVariants.prod4477ValueProposition: {
      ReactContent = (
        <VariantMaxWidthContainer>
          <BlogValuePropositionCard onClick={onCTAClick} dataGtmClick={dataGtmClick}>
            <ValuePropositionDisplay ctaValueProposition={ctaValueProposition} />
          </BlogValuePropositionCard>
        </VariantMaxWidthContainer>
      );
      break;
    }
    case BlogCTAValuePropositionVariants.prod4477ValuePropositionAddressInput: {
      ReactContent = (
        <VariantMaxWidthContainer>
          <BlogValuePropositionCard
            onClick={onCTAClick}
            gtmClasses={gtmClasses}
            dataGtmClick={dataGtmClick}
          >
            <ValuePropositionDisplay ctaValueProposition={ctaValueProposition} />
            <InputBox
              centerLabel={true}
              isLong={true}
              name="address"
              // eslint-disable-next-line react/jsx-no-bind
              onChange={onInputChange}
              placeholder={t('marketingPages:requestFormEntryForm.placeholder')}
            />
          </BlogValuePropositionCard>
        </VariantMaxWidthContainer>
      );
      break;
    }
  }
  return <SkeletonLoader isLoading={isConvertLoading}>{ReactContent}</SkeletonLoader>;
};

const VariantMaxWidthContainer: FC = ({ children }) => (
  <div className={style['variant-max-width-container']}>{children}</div>
);

const ValuePropositionDisplay: FC<
  Pick<BlogCTAValuePropositionVariantSwitcherProps, 'ctaValueProposition'>
> = ({ ctaValueProposition: { valuePropositionTitle, valuePropositionDescription } }) => (
  <section className={style['blog-value-prop-container']}>
    <p className={style.text} style={{ fontWeight: 'bold', fontSize: '20px' }}>
      {valuePropositionTitle}
    </p>
    <p className={style.text}>{valuePropositionDescription}</p>
  </section>
);

export default BlogCTAValuePropositionVariantSwitcher;
